import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { Colors, Dark, type ThemeColors } from '@/shared/constants/Colors'

export type ThemePreference = 'auto' | 'light' | 'dark'
type ColorScheme = 'light' | 'dark'

interface ThemeContextValue {
  preference: ThemePreference
  colorScheme: ColorScheme
  darkMode: boolean
  /** The mobile theme object (Colors / Dark) for inline styles that need raw hex values. */
  theme: ThemeColors
  setPreference: (p: ThemePreference) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)
const STORAGE_KEY = 'theme_preference'

function getSystemScheme(): ColorScheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => {
    try {
      return (localStorage.getItem(STORAGE_KEY) as ThemePreference) || 'auto'
    } catch {
      return 'auto'
    }
  })
  const [systemScheme, setSystemScheme] = useState<ColorScheme>(getSystemScheme)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => setSystemScheme(mq.matches ? 'dark' : 'light')
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  const colorScheme: ColorScheme = preference === 'auto' ? systemScheme : preference

  useEffect(() => {
    document.documentElement.classList.toggle('dark', colorScheme === 'dark')
    const meta = document.querySelector('meta[name="theme-color"]')
    if (meta) meta.setAttribute('content', colorScheme === 'dark' ? '#080808' : '#E74C3C')
  }, [colorScheme])

  const setPreference = (p: ThemePreference) => {
    setPreferenceState(p)
    try {
      localStorage.setItem(STORAGE_KEY, p)
    } catch {
      /* ignore */
    }
  }

  return (
    <ThemeContext.Provider value={{ preference, colorScheme, darkMode: colorScheme === 'dark', theme: colorScheme === 'dark' ? Dark : Colors, setPreference }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
